import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { desasignarSocioCaja, obtenerCajasDeSocio, obtenerCofresSocioMirror } from "../../services/cajaService.tsx";
import { Caja } from "../../types/caja.tsx";
import { useSocioContext } from "../../context/socioContext.tsx";
import { Building2, Info, Home } from "lucide-react";
import socioService from "../../services/socioService.tsx";
import { useAuth } from "../../context/AuthProvider.tsx";
import { Link } from "react-router-dom";

const COFRE_LETRA_ID_MAP: Record<string, string> = {
  "0": "A",
  "1": "B",
  "2": "C",
  "3": "D",
  "4": "E",
  "5": "F",
  "6": "G",
  "7": "H",
  "8": "I",
  "9": "J",
  "24": "X",
  "26": "Z",
};

const CajasPorSocio: React.FC = () => {
  const { socio } = useSocioContext();
  const socioLocal = socioService.getSelectedSocioFromLocalStorage();
  const socioActivo = socio?.socio_id ? socio : socioLocal;
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAuth();
  const [cajas, setCajas] = useState<Caja[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [desasignandoId, setDesasignandoId] = useState<string | null>(null);
  const [cofresResumen, setCofresResumen] = useState<any | null>(null);
  const [fuenteDatos, setFuenteDatos] = useState<"mirror" | "legacy" | null>(null);

  const resumen = useMemo(() => {
    const total = cajas.length;
    const ocupadas = cajas.filter((c) =>
      String(c.estado || "").toLowerCase().includes("ocup")
    ).length;
    const disponibles = total - ocupadas;
    const titulares = cajas.filter((c: any) => c.esTitular).length;
    const adicionales = total - titulares;
    return { total, ocupadas, disponibles, titulares, adicionales };
  }, [cajas]);

  const badgeEstado = (estado?: string) => {
    const value = String(estado || "").toLowerCase();
    if (value.includes("ocup")) return "bg-rose-50 text-rose-700";
    if (value.includes("dispon")) return "bg-emerald-50 text-emerald-700";
    return "bg-slate-100 text-slate-700";
  };

  const estadoContratoLegacyLabel = (estado: any, fechaFin?: string) => {
    const n = Number(estado);
    const hoy = new Date();
    const vencido = fechaFin ? new Date(fechaFin) < hoy : false;

    if (n === 0) return vencido ? "Vencido" : "Vigente";
    if (n === 1) return "Anulado";
    if (n === 2) return "Finalizado";
    return estado ?? "—";
  };

  const modalidadContratoLegacyLabel = (modalidad: any) => {
    const n = Number(modalidad);
    if (n === 0) return "Manual";
    if (n === 1) return "Automática";
    return modalidad ?? "—";
  };

  const normalizarCodigoCofre = (valor: any) => {
    const raw = String(valor ?? "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "")
      .replace(/[^A-Z0-9-]/g, "");
    if (!raw) return "—";
    return raw;
  };

  const resolverLetraCofre = (valor: any) => {
    const raw = String(valor ?? "").trim().toUpperCase();
    if (!raw) return "";
    if (/^[A-ZÑ]$/.test(raw)) return raw;
    return COFRE_LETRA_ID_MAP[raw] || raw;
  };

  const partesCofre = (caja: any) => {
    const letra = resolverLetraCofre(caja?.cofreLetra);
    const numero = caja?.cofreNumero;

    if (letra || numero) {
      return {
        posicion: letra || "—",
        numero: numero ?? "—",
        codigo: normalizarCodigoCofre(`${letra || ""}${numero ?? ""}`),
      };
    }

    const raw = String(caja?.numero ?? "").trim().toUpperCase();
    const match = raw.match(/^([A-ZÑ])\s*[-/]?\s*(\d+)$/);
    if (match) {
      return {
        posicion: match[1],
        numero: match[2],
        codigo: normalizarCodigoCofre(`${match[1]}${match[2]}`),
      };
    }

    return {
      posicion: "—",
      numero: raw || "—",
      codigo: normalizarCodigoCofre(raw),
    };
  };

  useEffect(() => {
    const fetchCajas = async () => {
      if (!socioActivo?.socio_id) return;
      try {
        setLoading(true);
        setError(null);
        setMsg(null);
        const token = await getAccessTokenSilently();

        const parseLegacy = (data: any[]) =>
          (data || []).map((item: any) => {
            const caja = item.caja || item;
            const tamano = caja.tamano || {};
            const tamanoNombre = tamano.nombre || "—";
            const tamanoColor = tamanoNombre.toLowerCase().includes("peque")
              ? "bg-blue-100 text-blue-800"
              : tamanoNombre.toLowerCase().includes("mediana") || tamanoNombre.toLowerCase().includes("medio")
              ? "bg-emerald-100 text-emerald-800"
              : "bg-purple-100 text-purple-800";
            return {
              id: caja.id ?? caja.caja_id,
              numero: caja.numero,
              estado: caja.estado,
              ubicacion: caja.ubicacion,
              nota: item.nota ?? caja.nota,
              tamano,
              tamanoNombre,
              tamanoColor,
              precioMensual: tamano.precio_mensual ?? tamano.precioMensual,
              precioAnual: tamano.precio_anual ?? tamano.precioAnual,
              socioCajaId: item.socio_caja_id ?? item.id,
              esTitular: item.es_titular ?? item.esTitular ?? true,
              fechaInicio: item.fecha_inicio ?? item.fechaInicio,
              fechaFin: item.fecha_fin ?? item.fechaFin,
            } as any;
          });

        const parseMirror = (mirror: any) =>
          (mirror?.cajasAsignadas || []).map((item: any) => {
            const tamanoNombre = item.tamano_nombre || "—";
            const tamanoColor = tamanoNombre.toLowerCase().includes("peque")
              ? "bg-blue-100 text-blue-800"
              : tamanoNombre.toLowerCase().includes("mediana") || tamanoNombre.toLowerCase().includes("medio")
              ? "bg-emerald-100 text-emerald-800"
              : "bg-purple-100 text-purple-800";

            return {
              id: item.caja_id ?? item.socio_caja_id,
              cajaIdReal: item.caja_id,
              numero: item.caja_numero,
              estado: item.caja_estado,
              ubicacion: item.caja_ubicacion,
              nota: item.nota,
              tamano: { nombre: item.tamano_nombre },
              tamanoNombre,
              tamanoColor,
              precioMensual: undefined,
              precioAnual: undefined,
              socioCajaId: item.socio_caja_id,
              esTitular: item.es_titular ?? true,
              fechaInicio: item.fecha_inicio,
              fechaFin: item.fecha_fin,
              origen: item.origen,
              contratoId: item.contrato_id,
              contratoNumero: item.contrato_numero,
              contratoEstado: item.contrato_estado,
              contratoModalidad: item.contrato_modalidad,
              cofreLetra: item.cofre_letra,
              cofreNumero: item.cofre_numero,
            } as any;
          });

        let parsed: any[] = [];

        try {
          const mirror = await obtenerCofresSocioMirror(socioActivo.socio_id, token, 50);
          setCofresResumen(mirror?.resumen || null);
          setFuenteDatos("mirror");
          parsed = parseMirror(mirror);
        } catch {
          setFuenteDatos("legacy");
          setCofresResumen(null);
          const data = await obtenerCajasDeSocio(socioActivo.socio_id, token);
          parsed = parseLegacy(data || []);
        }

        setCajas(parsed as any);
      } catch (err) {
        console.error("Error al cargar cajas del socio", err);
        setError("No pudimos cargar tus cajas. Intenta nuevamente.");
      } finally {
        setLoading(false);
      }
    };
    fetchCajas();
  }, [socioActivo?.socio_id, getAccessTokenSilently]);

  const desasociarCaja = async (caja: any) => {
    const cajaId = Number(caja?.cajaIdReal ?? caja?.id);
    if (!isAdmin || !socioActivo?.socio_id || !Number.isFinite(cajaId) || caja?.origen === "legacy") return;

    const actionId = `${socioActivo.socio_id}:${cajaId}`;
    try {
      setDesasignandoId(actionId);
      setError(null);
      setMsg(null);
      const token = await getAccessTokenSilently();
      await desasignarSocioCaja(cajaId, Number(socioActivo.socio_id), token);
      setCajas((prev) => prev.filter((c: any) => Number(c.id) !== cajaId));
      setMsg("Caja desasociada del socio.");
    } catch (err: any) {
      console.error("Error al desasociar caja", err);
      const apiMsg =
        err?.response?.data?.message || err?.message || "No se pudo desasociar la caja.";
      setError(apiMsg);
    } finally {
      setDesasignandoId(null);
    }
  };

  if (!socioActivo?.socio_id) {
    return (
      <div className="max-w-5xl mx-auto p-4">
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          No hay un socio seleccionado. Seleccioná un socio para ver sus cajas.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      <nav className="flex items-center text-sm text-slate-600" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-2">
          <li className="inline-flex items-center">
            <Link to="/inicio" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
              <Home className="h-4 w-4" />
              Inicio
            </Link>
          </li>
          <li className="text-slate-400">/</li>
          <li>
            <Link to="/admin" className="text-blue-600 hover:text-blue-800">
              Panel de Administración
            </Link>
          </li>
          <li className="text-slate-400">/</li>
          <li className="font-semibold text-slate-700">Cajas por socio</li>
        </ol>
      </nav>
      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
              Cajas de seguridad
            </p>
            <h1 className="text-3xl font-semibold text-slate-900">
              {socioActivo?.socio_nombre || "Socio"} · {socioActivo?.socio_id}
            </h1>
            <p className="text-sm text-slate-500">
              Listado de cajas asignadas al socio.
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
              <span className={`rounded-full px-2 py-1 font-semibold ${fuenteDatos === "mirror" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                Fuente: {fuenteDatos === "mirror" ? "mirror (/cajas/cofres/socio/:id)" : "cofres (/cajas/socio/:id)"}
              </span>
              {cofresResumen && (
                <span className="text-slate-500">
                  Movimientos: {cofresResumen.cantidad_movimientos ?? 0} · Total: ${Number(cofresResumen.total_importe ?? 0).toLocaleString("es-AR")} · A cobrar: ${Number(cofresResumen.total_importe_cobrar ?? 0).toLocaleString("es-AR")}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-600">
            <span className="rounded-full bg-slate-100 px-3 py-1">
              Total: {resumen.total}
            </span>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
              Disponibles: {resumen.disponibles}
            </span>
            <span className="rounded-full bg-rose-50 px-3 py-1 text-rose-700">
              Ocupadas: {resumen.ocupadas}
            </span>
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700">
              Titular: {resumen.titulares}
            </span>
            {resumen.adicionales > 0 && (
              <span className="rounded-full bg-slate-100 px-3 py-1">
                Adicional: {resumen.adicionales}
              </span>
            )}
          </div>

        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {msg && (
        <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {msg}
        </div>
      )}

      {loading ? (
        <div className="rounded-lg bg-white p-4 text-sm text-slate-600 shadow-sm ring-1 ring-slate-200">
          Cargando cajas...
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {cajas.map((caja: any) => {
            const partes = partesCofre(caja);
            return (
            <div
              key={caja.id}
              className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-slate-400">
                      Caja de seguridad
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="min-w-[140px] rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-center">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600">Posición (módulo)</p>
                        <p className="text-xl font-bold leading-tight text-indigo-900">{partes.posicion}</p>
                      </div>
                      <div className="min-w-[140px] rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">N° de cofre</p>
                        <p className="text-xl font-bold leading-tight text-slate-900">{partes.numero}</p>
                      </div>
                      <div className="ml-auto flex items-center">
                        <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${badgeEstado(caja.estado)}`}>
                          {caja.estado || "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${caja.tamanoColor}`}
                >
                  {caja.tamanoNombre || caja.tamano?.nombre || "Sin tamaño"}
                </span>
              </div>
              <div className="grid gap-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-700 sm:grid-cols-2">
                <p>
                  Precio mensual:{" "}
                  <span className="font-semibold">
                    ${caja.precioMensual?.toLocaleString("es-AR") || "—"}
                  </span>
                </p>
                <p>
                  Inicio:{" "}
                  <span className="font-semibold">{caja.fechaInicio || "—"}</span>
                </p>
                <p>
                  Rol:{" "}
                  <span className="font-semibold">
                    {caja.esTitular ? "Titular" : "Adicional"}
                  </span>
                </p>
                <p>
                  Vence:{" "}
                  <span className="font-semibold">{caja.fechaFin || "—"}</span>
                </p>
              </div>

              {caja.origen === "legacy" && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <div className="mb-1 inline-flex rounded-full bg-amber-100 px-2 py-0.5 font-semibold">
                    Cofres
                  </div>
                  <div className="grid gap-1 sm:grid-cols-2">
                    <p>Nro de contrato: <span className="font-semibold">{caja.contratoNumero || "—"}</span></p>
                    <p>Id interno: <span className="font-semibold">{caja.contratoId || "—"}</span></p>
                    <p>Estado contrato: <span className="font-semibold">{estadoContratoLegacyLabel(caja.contratoEstado, caja.fechaFin)}{caja.contratoEstado !== undefined && caja.contratoEstado !== null ? ` (${caja.contratoEstado})` : ""}</span></p>
                    <p>Modalidad: <span className="font-semibold">{modalidadContratoLegacyLabel(caja.contratoModalidad)}{caja.contratoModalidad !== undefined && caja.contratoModalidad !== null ? ` (${caja.contratoModalidad})` : ""}</span></p>
                    <p>Código de cofre: <span className="font-semibold">{partes.codigo}</span></p>
                  </div>
                  <details className="mt-2 rounded-lg border border-amber-200 bg-white/70 p-2">
                    <summary className="cursor-pointer select-none font-semibold">Ver cómo está formado</summary>
                    <div className="mt-2 grid gap-1 sm:grid-cols-2">
                      <p>Posición (módulo): <span className="font-semibold">{partes.posicion}</span></p>
                      <p>Número de cofre: <span className="font-semibold">{partes.numero}</span></p>
                      <p>Código final: <span className="font-semibold">{partes.codigo}</span></p>
                      <p>Nro de contrato: <span className="font-semibold">{caja.contratoNumero || "—"}</span></p>
                    </div>
                  </details>
                </div>
              )}
              {isAdmin && caja.origen !== "legacy" && Number.isFinite(Number(caja.cajaIdReal ?? caja.id)) && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => desasociarCaja(caja)}
                    disabled={desasignandoId === `${socioActivo?.socio_id}:${Number(caja.cajaIdReal ?? caja.id)}`}
                    className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 shadow-sm hover:bg-rose-100 disabled:opacity-70"
                  >
                    {desasignandoId === `${socioActivo?.socio_id}:${Number(caja.cajaIdReal ?? caja.id)}` ? "Desasociando..." : "Desasociar socio"}
                  </button>
                </div>
              )}
            </div>
            );
          })}
          {cajas.length === 0 && !error && (
            <div className="col-span-full rounded-xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
              <Info className="mx-auto mb-2 h-5 w-5 text-slate-400" />
              Este socio no tiene cajas asignadas.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CajasPorSocio;
